import type { Dayjs } from 'dayjs';

import { isBeforeMetricsUpgrade } from '@/models/facebook/metrics-upgrade';

export const tooltips = {
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noUserPermission: "Looks like you don't have permission to use this feature!",
  connectCallToAction:
    "Dash Social brings you all of the insights you love, including Facebook! Ask your Dash Social administrator to provide you access. Or if this feature isn't in your package, contact your Customer Success Representative to try it out!",
  avgEngagementRate:
    'The average engagement rate for your organic posts published during the selected date range\n' +
    '\n' +
    'Engagement Rate = Total Engagements / Impressions',
  averageEngagementRatePublic:
    'The Total Engagements / Page Fans of posts published during the Reporting Period. This excludes Post Clicks and Impressions to ensure a fair comparison with competitors based on publicly available data.',
  averageEffectiveness:
    'The average effectiveness rate for your organic posts published during the selected date range\n' +
    '\n' +
    'Effectiveness Rate = (Total Engagements + Video Views) / Reach\n',
  linkClicks:
    'The total number of link clicks for your organic posts published during the selected date range',
  netNewFans: 'The net total of new fans your page has accumulated during the selected date range',
  netNewFollowers:
    'The net total of new followers your page has accumulated during the selected date range',
  avgDailyPostReach:
    "The average daily reach for any of your page's organic posts during the selected date range",
  engagements:
    "The number of total engagements on any of your page's organic and paid posts during the selected date range",
  impressionsTooltip: `The total number of impressions for any of your page's organic posts during the selected date range`,
} as const;

export const promotedTopLineStatsTooltipsDeprecated = {
  engagements:
    'The total number of engagements on your organic posts published during the selected date range' +
    '\n\n' +
    'Engagements = Reactions + Comments + Shares + Post Clicks',
  avgEngagementRate:
    'The average engagement rate for your organic posts published during the selected date range' +
    '\n\n' +
    'Engagement Rate = Engagements / Impressions',
  avgEffectiveness:
    'The average effectiveness rate for your organic posts published during the selected date range' +
    '\n\n' +
    'Effectiveness Rate = (Engagements + Video Views) / Reach',
  avgReach:
    "The average reach for any of your page's organic and promoted posts published during the selected date range" +
    '\n\n' +
    'Average Reach = Total Reach / Number of Posts published during the time frame',
  impressions:
    'The total number of impressions for your organic and promoted posts published during the selected date range',
  linkClicks:
    'The total number of link clicks for your organic and promoted posts published during the selected date range',
} as const;

export const promotedTopLineStatsTooltips = {
  engagements:
    'The sum of engagements on your organic and promoted posts' +
    '\n\n' +
    'Engagements = Reactions + Shares + Comments' +
    '\n\n' +
    'Reactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
  avgEngagementRate:
    'The average engagement rate for your organic and promoted posts' +
    '\n\n' +
    'Engagement Rate = (Engagements / Views) / Number of Posts',
  avgEffectiveness:
    'The average percentage of users that saw your organic and promoted posts and engaged with it' +
    '\n\n' +
    'Average Effectiveness = (Engagements / Viewers) / Number of Posts',
  avgReach:
    'The average reach of your organic and promoted posts' +
    '\n\n' +
    'Avg. Reach = Reach / Number of Posts',
  views:
    'The number of times your organic and promoted videos were played for at least 1ms plus the number of times your photo and text posts were on screen',
  linkClicks:
    'The number of times a Facebook user clicked and followed the link, or link preview to the destination URL on your organic and promoted posts',
  avgViewers: ({ startDate }: { startDate?: Dayjs | null } = {}) => {
    const tooltip =
      'The average number of unique accounts that have viewed your posts at least once.' +
      '\n\n' +
      'Avg. Viewers = Viewers / Number of Posts';

    if (isBeforeMetricsUpgrade(startDate)) {
      return `${tooltip}\n\nNote: Values before July 31, 2025 reflect Reach, which Meta replaced with Viewers.`;
    }
    return tooltip;
  },
} as const;

export const promotedMediaPopupTooltips = {
  amountSpent:
    "The total amount of money you've spent boosting your posts. Recent spend may be estimated for up to 48 hours.",
  clicks:
    'The total number of clicks this promoted post received. This can include link clicks, clicks to your profile, and more.',
  comments: 'The number of comments your posts received',
  commentsOrganic: 'The number of organic comments your posts received',
  commentsPromoted: 'The number of promoted comments your posts received',
  costPerThruPlay:
    'The average cost per ThruPlay for this video\n' +
    '\n' +
    'Cost Per ThruPlay = Amount spent / ThruPlays',
  cpc:
    'The average cost per click on your promoted posts\n' +
    '\n' +
    'CPC = Amount Spent / Total Promoted Clicks',
  cpm:
    'The average cost per 1,000 impressions on your promoted posts\n' +
    '\n' +
    'CPM = Amount Spent / Total Promoted Impressions × 1,000',
  ctr:
    'The percentage of times people clicked your promoted posts after seeing them\n' +
    '\n' +
    'CTR = Total Clicks / Promoted Impressions',
  effectivenessNonVideo:
    "The rate of this post's engagement based on the unique accounts reached\n" +
    '\n' +
    'Effectiveness = (Likes + Comments + Saves + Shares) / Reach',
  effectivenessVideo:
    "The rate of this post's views and engagement based on the unique accounts reached\n" +
    '\n' +
    'Effectiveness Rate = (Total Engagements + Video Views) / Reach',
  effectivenessOrganic:
    'The percentage of users that saw your organic post and engaged with it\n' +
    '\n' +
    'Effectiveness = Engagements / Reach\n' +
    '\n' +
    'Note: This metric is discontinued and unavailable for posts after June 15th, 2026.',
  effectivenessPromoted:
    'The percentage of users that saw your promoted post and engaged with it\n' +
    '\n' +
    'Effectiveness = Engagements / Reach',
  engagementsOrganic:
    'The total number of organic engagements on this post\n' +
    '\n' +
    'Engagements = Reactions + Comments + Shares + Post Clicks',
  engagementsPromoted:
    'The total number of promoted engagements on this post\n' +
    '\n' +
    'Engagements = Reactions + Comments + Shares\n',
  engagementRate:
    'The average organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Impressions',
  engagementRateOrganic:
    'The engagement rate for your organic post\n\nEngagement Rate = Engagements / Views',
  engagementRatePromoted:
    'The average promoted engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Promoted Engagements / Promoted  Impressions',
  frequency: 'The average number of times people saw your promoted posts',
  impressions: 'The total number of impressions this post received',
  pageLikes: 'The total number of likes this post received',
  reachOrganicDeprecated:
    'The number of unique accounts that saw your organic post' +
    '\n\nNote: This metric is discontinued and unavailable for posts after June 15th, 2026.',
  viewersTotal: ({ datePosted }: { datePosted?: Dayjs | null } = {}) => {
    const tooltip = 'The number of unique accounts that have viewed this post at least once';
    if (isBeforeMetricsUpgrade(datePosted)) {
      return `${tooltip}\n\nNote: Values before July 31, 2025 reflect Reach, which Meta replaced with Viewers.`;
    }
    return tooltip;
  },
  reachPromotedAds:
    'The number of unique accounts that have viewed this post through paid promotions at least once' +
    '\n\n' +
    'Note: This metric uses Reach from Ad campaigns',
  reactions:
    'The number of reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  reactionsOrganic:
    'The number of organic reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  reactionsPromoted:
    'The number of promoted reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  shares: 'The number of times your posts were shared',
  sharesOrganic: 'The number of times your organic posts were shared',
  sharesPromoted: 'The number of times your promoted posts were shared',
  thruPlays: 'The number of times your posts were watched to completion or for at least 15 seconds',
  videoCompleteViews:
    'The number of times your videos were played from the beginning to 95% or more of their length',
  videoCompleteViewsOrganic:
    'The number of times your organic videos were played from the beginning to 95% or more of their length',
  videoCompleteViewsPromoted:
    'The number of times your promoted videos were played from the beginning to 95% or more of their length',
  videoPlays25: 'The total number of times your promoted video was played to 25% of its length',
  videoPlays50: 'The total number of times your promoted video was played to 50% of its length',
  videoPlays75: 'The total number of times your promoted video was played to 75% of its length',
  videoPlays100: 'The total number of times your promoted video was played to 100% of its length',
  videoViews: 'The number of times your videos were viewed',
  videoViewsOrganic:
    'The number of times your organic videos were played for at least 3 seconds, or for nearly their total length if shorter than 3 seconds. This metric excludes replays.',
  videoViewsPromoted:
    'The number of times your promoted videos were played for at least 3 seconds, or for nearly their total length if shorter than 3 seconds. This metric excludes replays.',
  paidAndOrganicViewsVideos:
    'The number of times your organic and promoted videos were played for at least 1ms',
  organicViewsVideos: 'The number of times your organic videos were played for at least 1ms',
  paidViewsVideos: 'The number of times your promoted videos were played for at least 1ms',
  paidAndOrganicViewsNonVideos:
    'The number of times your organic and promoted photo and text posts were displayed on screen',
  organicViewsNonVideos:
    'The number of times your organic photo and text posts were displayed on screen',
  paidViewsNonVideos:
    'The number of times your promoted photo and text posts were displayed on screen',
  // Individual reaction type tooltips
  reactionAngry: 'The number of times people used an Angry reaction to your post',
  reactionHaha: 'The number of times people used a Haha reaction to your post',
  reactionLike: 'The number of times people used a Like reaction to your post',
  reactionLove: 'The number of times people used a Love reaction to your post',
  reactionSad: 'The number of times people used a Sad reaction to your post',
  reactionWow: 'The number of times people used a Wow reaction to your post',
} as const;

export const defaultGraphMetric = 'TOTAL_ENGAGEMENTS' as const;

export const filterTypes = {
  GRAPH: 'Graph',
  DATE: 'Date',
  SCALE: 'Scale',
} as const;
export const timeScaleTypes = {
  DAILY: {
    value: 'DAILY',
    label: 'By Day',
  },
  MONTHLY: {
    value: 'MONTHLY',
    label: 'By Month',
  },
} as const;

export const facebookMediaCardMetricsAds = [
  'paidAndOrganicEngagementRate',
  'paidAndOrganicImpressions',
  'paidAndOrganicEngagements',
  'paidAndOrganicReach',
] as const;

export const facebookMediaCardDefaults = {
  metrics: ['engagementRate', 'impressions', 'totalEngagements', 'reach'],
  labels: {
    engagementRate: 'Engagement Rate',
    totalEngagements: 'Total Engagements',
    paidAndOrganicViews: 'Views',
  },
} as const;

export const facebookCompetitiveMediaCardDefaults = {
  labels: {
    comments: 'Comments',
    engagementRatePublic: 'Engagement Rate',
    reactions: 'Reactions',
    shares: 'Shares',
  },
  metrics: ['comments', 'engagementRatePublic', 'reactions', 'shares'],
} as const;

export const yourPostsSortOptions = [
  {
    value: 'FACEBOOK_REELS_FB_REELS_TOTAL_PLAYS',
    label: 'All Plays',
    field: 'reel.fbReelsTotalPlays',
  },
  {
    value: 'FACEBOOK_REELS_POST_VIDEO_AVG_TIME_WATCHED',
    label: 'Avg. Time Watched',
    field: 'reel.postVideoAvgTimeWatched',
  },
  {
    value: 'FACEBOOK_COMMENTS',
    label: 'Comments',
    field: 'comments',
  },
  {
    value: 'FACEBOOK_EFFECTIVENESS',
    label: 'Effectiveness',
    field: 'effectiveness',
  },
  {
    value: 'FACEBOOK_REELS_EFFECTIVENESS',
    label: 'Effectiveness - Reels',
    field: 'reel.effectiveness',
  },
  {
    value: 'FACEBOOK_ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    field: 'engagementRate',
  },
  {
    value: 'FACEBOOK_IMPRESSIONS',
    label: 'Impressions',
    field: 'impressions',
  },
  {
    value: 'FACEBOOK_LINK_CLICKS',
    label: 'Link Clicks - Total',
    field: 'linkClicks',
  },
  {
    value: 'FACEBOOK_OTHER_CLICKS',
    label: 'Other Clicks',
    field: 'otherClicks',
  },
  {
    value: 'FACEBOOK_PHOTO_VIEW_CLICKS',
    label: 'Photo View Clicks',
    field: 'photoViewClicks',
  },
  {
    value: 'FACEBOOK_BLUE_REELS_PLAYS_COUNT',
    label: 'Plays',
    field: 'reel.blueReelsPlayCount',
  },
  {
    value: 'FACEBOOK_POST_CLICKS',
    label: 'Post Clicks',
    field: 'postClicks',
  },
  {
    value: 'FACEBOOK_REACH',
    label: 'Reach',
    field: 'reach',
  },
  {
    value: 'FACEBOOK_REACTIONS',
    label: 'Reactions',
    field: 'reactions',
  },
  {
    value: 'DATE',
    label: 'Recently Published',
  },
  {
    value: 'FACEBOOK_REELS_REPLAY_COUNT',
    label: 'Replays',
    field: 'reel.fbReelsReplayCount',
  },
  {
    value: 'FACEBOOK_SHARES',
    label: 'Shares',
    field: 'shares',
  },
  {
    value: 'FACEBOOK_TOTAL_ENGAGEMENTS',
    label: 'Total Engagements',
    field: 'totalEngagements',
  },
  {
    value: 'FACEBOOK_VIDEO_COMPLETE_VIEWS',
    label: 'Video Complete Views',
    field: 'videoCompleteViews',
  },
  {
    value: 'FACEBOOK_VIDEO_VIEWS',
    label: 'Video Views',
    field: 'videoViews',
  },
] as const;

export const facebookGalleryCardDefaults = {
  metrics: [
    'fbTotalEngagements',
    'fbAvgEngagementRate',
    'fbAvgEffectiveness',
    'fbAvgReach',
    'fbTotalImpressions',
  ],
  labels: {
    fbTotalEngagements: 'Total Engagements',
    fbAvgEngagementRate: 'Avg. Engagement Rate',
    fbAvgEffectiveness: 'Avg. Effectiveness',
    fbAvgReach: 'Avg. Reach',
    fbTotalImpressions: 'Impressions',
  },
} as const;

export const facebookMetricFormats = {
  fbAvgEngagementRate: 'percent',
  fbAvgEffectiveness: 'percent',
} as const;

const routeNames = {
  YOUR_POSTS: 'facebook.yourPosts',
} as const;

export default {
  routeNames,
  facebookMediaCardDefaults,
} as const;

export const facebookCommentRequiredScopes = [
  'pages_manage_engagement',
  'pages_read_engagement',
  'pages_manage_metadata',
];

export const facebookDirectMessageRequiredScopes = ['pages_messaging', 'pages_manage_metadata'];

export const facebookTopLineStatsMetrics = [
  {
    keys: {
      netNew: 'NET_NEW_FOLLOWERS',
      total: 'TOTAL_FOLLOWERS',
    },
    value: 'NET_NEW_FOLLOWERS',
    label: 'Net New Followers',
    tooltip: tooltips.netNewFollowers,
  },
  {
    keys: {
      organic: 'ORGANIC_ENGAGEMENTS_POSTS_V2',
      promoted: 'PAID_ENGAGEMENTS_POSTS',
      total: 'TOTAL_ENGAGEMENTS_POSTS_V2',
    },
    value: 'TOTAL_ENGAGEMENTS_POSTS_V2',
    label: 'Engagements - Total',
    tooltip: promotedTopLineStatsTooltips.engagements,
  },
  {
    keys: {
      organic: 'AVG_ORGANIC_ENGAGEMENT_RATE_V2',
      promoted: 'AVG_PAID_ENGAGEMENT_RATE_V2',
      total: 'AVG_ENGAGEMENT_RATE_V2',
    },
    value: 'AVG_ENGAGEMENT_RATE_V2',
    label: 'Avg. Eng. Rate - Total',
    percent: true,
    tooltip: promotedTopLineStatsTooltips.avgEngagementRate,
  },
  {
    keys: {
      organic: 'AVG_ORGANIC_EFFECTIVENESS_V2',
      promoted: 'AVG_PAID_EFFECTIVENESS_V2',
      total: 'AVG_EFFECTIVENESS_V2',
    },
    value: 'AVG_EFFECTIVENESS_V2',
    label: 'Avg. Effectiveness - Total',
    percent: true,
    tooltip: promotedTopLineStatsTooltips.avgEffectiveness,
  },
  {
    keys: {
      total: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    },
    value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    label: 'Avg. Viewers - Total',
    tooltip: promotedTopLineStatsTooltips.avgViewers,
    format: 'integer',
  },
  {
    keys: {
      organic: 'ORGANIC_VIEWS_BY_POST',
      promoted: 'PAID_VIEWS_BY_POST',
      total: 'PAID_AND_ORGANIC_VIEWS_BY_POST',
    },
    value: 'PAID_AND_ORGANIC_VIEWS_BY_POST',
    label: 'Views - Total',
    tooltip: promotedTopLineStatsTooltips.views,
  },
  {
    keys: {
      total: 'LINK_CLICKS',
    },
    value: 'LINK_CLICKS',
    label: 'Link Clicks - Total',
    tooltip: promotedTopLineStatsTooltips.linkClicks,
  },
] as const;

export const facebookInsightsMetrics = [
  {
    label: 'Amount Spent',
    tooltip:
      "The total amount of money you've spent on promoted posts published during the selected date range",
    metrics: {
      promoted: 'TOTAL_SPENT',
    },
  },
  {
    label: 'CPC',
    tooltip:
      'The average cost per click of your promoted posts published during the selected date range\n' +
      '\n' +
      'CPC = Amount Spent / Promoted Clicks',
    metrics: {
      promoted: 'COST_PER_CLICK',
    },
  },
  {
    label: 'CPM',
    tooltip:
      'The average cost per 1,000 impressions on your promoted posts published during the selected date range\n' +
      '\n' +
      'CPM = Amount Spent / Total Promoted Impressions x 1,000',
    metrics: {
      promoted: 'COST_PER_MILE',
    },
  },
  {
    label: 'CTR',
    tooltip:
      'The percentage of times people clicked your promoted posts published during the selected date range\n' +
      '\n' +
      'CTR = Clicks / Promoted Impressions',
    metrics: {
      promoted: 'CLICK_THROUGH_RATE',
    },
  },
  {
    label: 'Comments',
    tooltip:
      'The total number of times people commented on your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_COMMENTS',
      organic: 'ORGANIC_COMMENTS_POSTS_PAID',
    },
  },
  {
    label: 'Views',
    tooltip:
      'The number of times your promoted videos were played for at least 1ms plus the number of times your photo and text promoted posts were seen on screen',
    metrics: {
      promoted: 'PAID_VIEWS_BY_POST',
      organic: 'ORGANIC_VIEWS_POSTS_PAID',
    },
  },
  {
    label: 'Reactions',
    tooltip:
      'The total number of times people reacted to your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_REACTIONS',
      organic: 'ORGANIC_REACTIONS_POSTS_PAID',
    },
  },
  {
    label: 'Shares',
    tooltip:
      'The total number of times people shared your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_SHARES',
      organic: 'ORGANIC_SHARES_POSTS_PAID',
    },
  },
  {
    label: 'Video Views',
    tooltip:
      'The total number times your promoted videos published during the reporting period were played for at least 3 seconds, or for nearly its total length if shorter than 3 seconds. This excludes replays.',
    metrics: {
      promoted: 'PAID_VIDEO_VIEWS',
      organic: 'ORGANIC_VIDEO_VIEWS_POSTS_PAID',
    },
  },
] as const;

export const facebookOverviewMetricChartTitleOptions = [
  {
    label: 'Avg. Eng. Rate - Total',
    value: 'AVG_ENGAGEMENT_RATE_V2',
    format: 'percent',
    displayTotalLabel: false,
  },
  {
    label: 'Avg. Effectiveness - Total',
    value: 'AVG_EFFECTIVENESS_V2',
    format: 'percent',
    displayTotalLabel: false,
  },
  { label: 'Engagements - Total', value: 'TOTAL_ENGAGEMENTS_POSTS_V2' },
  { label: 'Link Clicks - Total', value: 'LINK_CLICKS' },
  {
    label: 'Avg. Viewers - Total',
    value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    format: 'integer',
    displayTotalLabel: false,
  },
  { label: 'Views - Total', value: 'PAID_AND_ORGANIC_VIEWS_BY_POST' },
] as const;

export const overviewCSVConfig = {
  metrics: {
    label: 'Metrics CSV',
    filenamePrefix: 'metrics_csv',
    metricsListOrganicOnlyDeprecated: [
      'TOTAL_ENGAGEMENTS',
      'AVG_ENGAGEMENT_RATE',
      'AVG_EFFECTIVENESS',
      'AVG_REACH',
      'IMPRESSIONS',
      'LINK_CLICKS',
    ],
    metricsListPromotedBreakdownDeprecated: [
      'ORGANIC_ENGAGEMENTS_POSTS',
      'AVG_ORGANIC_ENGAGEMENT_RATE',
      'AVG_ORGANIC_EFFECTIVENESS',
      'AVG_ORGANIC_POST_REACH',
      'AVG_PAID_POST_REACH',
      'AVG_PAID_AND_ORGANIC_POST_REACH',
      'ORGANIC_IMPRESSIONS_BY_POST',
      'PAID_IMPRESSIONS_BY_POST',
      'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST',
      'LINK_CLICKS',
    ],
    metricsListOrganicOnly: [
      { value: 'NET_NEW_FOLLOWERS' },
      { value: 'TOTAL_ENGAGEMENTS_POSTS_V2' },
      { value: 'AVG_ENGAGEMENT_RATE_V2' },
      { value: 'AVG_EFFECTIVENESS_V2' },
      { value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2' },
      { value: 'PAID_AND_ORGANIC_VIEWS_BY_POST' },
      { value: 'LINK_CLICKS' },
      { value: 'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST' },
    ],
    metricsListPromotedBreakdown: [
      { value: 'NET_NEW_FOLLOWERS' },
      { value: 'TOTAL_FOLLOWERS' },

      { value: 'ORGANIC_ENGAGEMENTS_POSTS_V2' },
      { value: 'PAID_ENGAGEMENTS_POSTS' },
      { value: 'TOTAL_ENGAGEMENTS_POSTS_V2' },

      { value: 'AVG_ORGANIC_ENGAGEMENT_RATE_V2' },
      { value: 'AVG_PAID_ENGAGEMENT_RATE_V2' },
      { value: 'AVG_ENGAGEMENT_RATE_V2' },

      { value: 'AVG_ORGANIC_EFFECTIVENESS_V2' },
      { value: 'AVG_PAID_EFFECTIVENESS_V2' },
      { value: 'AVG_EFFECTIVENESS_V2' },

      { value: 'AVG_PAID_POST_REACH_V2' },
      { value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2' },

      { value: 'ORGANIC_VIEWS_BY_POST' },
      { value: 'PAID_VIEWS_BY_POST' },
      { value: 'PAID_AND_ORGANIC_VIEWS_BY_POST' },

      { value: 'LINK_CLICKS' },

      { value: 'ORGANIC_IMPRESSIONS_BY_POST' },
      { value: 'PAID_IMPRESSIONS_BY_POST' },
      { value: 'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST' },

      { value: 'AVG_ORGANIC_POST_REACH' },
    ],
  },
} as const;
